import { ErrorMessage } from '@hookform/error-message';
import { useId } from 'react';
import type { FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { get, useFormContext } from 'react-hook-form';
import { RenderedContent } from '../../utils';
import { Checkbox, CheckboxProps, Field, LabelProps, TextError } from '../form';

export interface FieldCheckboxProps<TFormValues extends Record<string, any> = any> 
  extends Omit<CheckboxProps, 'label'>, LabelProps {
  name: Path<TFormValues> | string;
  validation?: RegisterOptions<FieldValues, string>;
  label: RenderedContent;
  className?: string;
}

/**
 * Checkbox connecté au formulaire avec validation automatique
 * - Validation requise automatique
 * - Désactivation auto pendant soumission  
 * - Gestion d'erreurs intégrée
 * - Génère automatiquement un ID unique
 */
export function FieldCheckbox({ 
  name, 
  label,
  required = true, 
  validation = {},
  disabled,
  description,
  className,
  children,
  ...props 
}: FieldCheckboxProps) {
  const methods = useFormContext();
  const id = useId();

  const rules = {
    ...validation,
    validate: {
      ...validation.validate,
      ...(required && {
        required: (value: boolean) => {
          if (value === true) return true;
          // Récupérer le message custom si fourni dans validation.required

          return typeof validation.required === 'string' ? validation.required : 'Ce champ est requis';
        }
      })
    }
  };

  // Supprimer required des rules pour éviter la validation classique
  delete rules.required;

  return (
    <Field className={className}>
      <Checkbox
        id={id}
        label={label}
        description={description}
        required={required}
        {...methods.register(name, rules)}
        disabled={methods.formState.isSubmitting || disabled}
        error={get(methods.formState.errors, name)}
        {...props}
      />
      <ErrorMessage
        errors={methods.formState.errors}
        name={name}
        render={({ message }) => <TextError>{message}</TextError>}
      />
      {children}
    </Field>
  );
}
